import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Camera, ImagePlus, Trash2, X, Images } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { fileToDataUrl, resizeImage, createThumbnail, formatDate, cn } from '../utils/formatters';
import { input, label, modalOverlay, modalContent, modalBody, modalFooter } from '../styles/components';
import type { ProjectPhoto } from '../types';

const PHASES = [
  { value: 'before', label: 'Antes' },
  { value: 'during', label: 'Durante' },
  { value: 'after', label: 'Final' },
] as const;

type Phase = (typeof PHASES)[number]['value'];

const PHASE_PILL: Record<Phase, string> = {
  before: '',
  during: 'b2b-pill--accent',
  after: 'b2b-pill--ok',
};

export function ProjectPhotos({ projectId }: { projectId: string }) {
  const { photos, addProjectPhoto, deleteProjectPhoto } = useCompany();
  const { user } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [phase, setPhase] = useState<Phase>('during');
  const [caption, setCaption] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState<ProjectPhoto | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const projectPhotos = photos.filter(p => p.projectId === projectId);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(e.target.files ?? []));
  };

  const reset = () => {
    setShowAdd(false);
    setPhase('during');
    setCaption('');
    setFiles([]);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!files.length) {
      setError('Selecciona al menos una imagen.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const dataUrl = await fileToDataUrl(file);
        const url = await resizeImage(dataUrl, 1600, 1600, 0.8);
        const thumbnailUrl = await createThumbnail(dataUrl, 400);
        await addProjectPhoto({
          projectId,
          phase,
          url,
          thumbnailUrl,
          caption: caption.trim() || undefined,
          uploadedBy: user?.id ?? '',
        });
      }
      reset();
    } catch {
      setError('No se pudieron guardar las fotos.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="b2b-panel">
      <div className="b2b-panel__head">
        <h2 className="text-lg font-semibold">Galería de evolución</h2>
        <button onClick={() => setShowAdd(true)} className="b2b-btn b2b-btn--primary b2b-btn--sm">
          <ImagePlus className="w-4 h-4" /> Añadir fotos
        </button>
      </div>

      {projectPhotos.length === 0 ? (
        <div className="p-8 text-center b2b-muted">
          <Images className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Sin fotos todavía. Sube imágenes del antes, durante y después de la obra.</p>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {PHASES.map(ph => {
            const items = projectPhotos.filter(p => p.phase === ph.value);
            if (items.length === 0) return null;
            return (
              <div key={ph.value}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={cn('b2b-pill', PHASE_PILL[ph.value])}>{ph.label}</span>
                  <span className="text-xs b2b-muted">{items.length} foto{items.length === 1 ? '' : 's'}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {items.map(photo => (
                    <div key={photo.id} className="group relative rounded-lg overflow-hidden border border-[var(--b2b-border)]">
                      <button type="button" onClick={() => setLightbox(photo)} className="block w-full">
                        <img
                          src={photo.thumbnailUrl || photo.url}
                          alt={photo.caption || 'Foto del proyecto'}
                          className="w-full h-32 object-cover"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (confirm('¿Eliminar esta foto?')) deleteProjectPhoto(photo.id); }}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[color:var(--b2b-danger)]"
                        aria-label="Eliminar foto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {photo.caption && (
                        <p className="px-2 py-1 text-xs b2b-muted truncate bg-[var(--b2b-surface-muted)]">
                          {photo.caption}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div className={modalOverlay}>
          <div className={cn(modalContent, 'max-w-md')} role="dialog" aria-modal="true">
            <form onSubmit={handleSave}>
              <div className={modalBody}>
                <h2 className="text-xl font-bold mb-4">Añadir fotos de evolución</h2>
                {error && <p className="mb-3 text-sm text-[color:var(--b2b-danger)]">{error}</p>}
                <div className="space-y-3">
                  <div>
                    <label className={label}>Fase de la obra</label>
                    <select className={input} value={phase} onChange={e => setPhase(e.target.value as Phase)}>
                      {PHASES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={label}>Fotos</label>
                    <label className={cn(input, 'flex items-center gap-2 cursor-pointer')}>
                      <Camera className="w-4 h-4 b2b-muted" />
                      <span className="truncate">{files.length ? `${files.length} imagen(es) seleccionada(s)` : 'Elegir o hacer foto…'}</span>
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={onPick}
                        className="sr-only"
                      />
                    </label>
                  </div>
                  <div>
                    <label className={label}>Descripción (opcional)</label>
                    <input className={input} value={caption} onChange={e => setCaption(e.target.value)} placeholder="Ej: Tabique derribado" />
                  </div>
                </div>
              </div>
              <div className={modalFooter}>
                <button type="button" onClick={reset} className="b2b-btn b2b-btn--ghost">Cancelar</button>
                <button type="submit" className="b2b-btn b2b-btn--primary" disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)}>
          <div className="max-h-full w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 text-white">
              <span className="text-sm">{lightbox.caption || phaseLabel(lightbox.phase)} · {formatDate(lightbox.createdAt)}</span>
              <button onClick={() => setLightbox(null)} className="p-1 rounded-full hover:bg-white/20" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <img src={lightbox.url} alt={lightbox.caption || 'Foto del proyecto'} className="w-full max-h-[80vh] object-contain rounded-lg" />
            <div className="mt-3 text-center">
              <button
                onClick={() => { if (confirm('¿Eliminar esta foto?')) { deleteProjectPhoto(lightbox.id); setLightbox(null); } }}
                className="b2b-btn b2b-btn--danger"
              >
                <Trash2 className="w-4 h-4" /> Eliminar foto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function phaseLabel(p: Phase): string {
  return PHASES.find(ph => ph.value === p)?.label ?? p;
}
